// src/features/agent-builder/components/fields/FileUploadField.tsx
import React, { useState, useCallback, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { UploadCloud } from 'lucide-react';
import type { UploadableFile } from '@dopomogai/types';
import { UploadedFileListItem } from '../UploadedFileListItem';

export interface FileUploadFieldProps {
  labelKey: string;
  descriptionTextKey?: string;
  files: UploadableFile[];
  onFilesAdded: (newFiles: File[]) => void;
  onFileRemoved: (fileId: string) => void;
  onFileRetry?: (fileId: string) => void;
  acceptedFileTypes?: string;
  maxFileSizeMB?: number;
  maxFiles?: number;
  className?: string;
  disabled?: boolean;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  labelKey,
  descriptionTextKey,
  files,
  onFilesAdded,
  onFileRemoved,
  onFileRetry,
  acceptedFileTypes = ".pdf,.txt,.docx",
  maxFileSizeMB = 10,
  maxFiles = 5,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation('agentBuilder');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();

  const handleFileSelectionLogic = (selectedNativeFiles: FileList | null) => {
    // ... (logic remains the same)
    if (selectedNativeFiles && !disabled) {
      let newFilesArray = Array.from(selectedNativeFiles);
      const sizeValidatedFiles = newFilesArray.filter(file => {
        if (file.size > maxFileSizeMB * 1024 * 1024) {
          console.warn(`File ${file.name} (size ${file.size}) exceeds max size of ${maxFileSizeMB}MB. Skipping.`);
          return false;
        }
        return true;
      });
      const remainingSlots = maxFiles - files.length;
      if (sizeValidatedFiles.length > remainingSlots) {
        console.warn(`Cannot add all ${sizeValidatedFiles.length} files. Max files limit of ${maxFiles} would be exceeded. Adding first ${remainingSlots} valid files.`);
        onFilesAdded(sizeValidatedFiles.slice(0, Math.max(0, remainingSlots)));
      } else if (sizeValidatedFiles.length > 0) {
        onFilesAdded(sizeValidatedFiles);
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onNativeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelectionLogic(event.target.files);
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || files.length >= maxFiles) return;
    e.preventDefault(); e.stopPropagation(); setIsDragging(true);
  }, [disabled, files.length, maxFiles]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || files.length >= maxFiles) return;
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  }, [disabled, files.length, maxFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || files.length >= maxFiles) return;
    e.preventDefault(); e.stopPropagation();
  }, [disabled, files.length, maxFiles]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || files.length >= maxFiles) return;
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelectionLogic(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  }, [disabled, files, onFilesAdded, maxFiles, maxFileSizeMB]);

  const triggerFileInput = () => {
    if (!disabled && files.length < maxFiles) fileInputRef.current?.click();
  };

  const canAddMoreFiles = files.length < maxFiles;
  const dropzoneDisabled = disabled || !canAddMoreFiles;

  const dropzoneBaseBorder = 'border-2 border-black border-dashed';
  const dropzoneDragActiveBorder = 'border-2 border-indigo-500 border-solid'; // Solid border when dragging

  return (
    <div className={clsx('w-full', className)}>
      {labelKey && (
        <label htmlFor={fieldId} className="block text-sm font-semibold text-slate-700 mb-1.5">
          {t(labelKey)}
        </label>
      )}

      {/* Interactive Dropzone Area - now a group for hover effects */}
      <div
        className={clsx(
          'relative mt-1 group', // GROUP for hover effects
          dropzoneDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!dropzoneDisabled ? triggerFileInput : undefined}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !dropzoneDisabled) triggerFileInput(); }}
        role={!dropzoneDisabled ? "button" : undefined}
        tabIndex={!dropzoneDisabled ? 0 : -1}
        aria-disabled={dropzoneDisabled}
        aria-labelledby={labelKey ? fieldId + '-label' : undefined} // Assuming label has id={fieldId + '-label'}
      >
        {/* Shadow Element - appears on hover/drag */}
        {!dropzoneDisabled && (
          <div
            className={clsx(
              "absolute inset-0 rounded-xl transition-all duration-150 ease-out",
              isDragging ? "bg-indigo-700" : "bg-black", // Shadow color can adapt to drag state
              (isDragging || !dropzoneDisabled) && "group-hover:translate-x-1 group-hover:translate-y-1",
              isDragging && "translate-x-1 translate-y-1" // Ensure shadow is visible immediately on drag
            )}
          />
        )}

        {/* Content Element - lifts on hover/drag */}
        <div
          className={clsx(
            'relative flex flex-col items-center justify-center px-6 py-8 rounded-xl',
            'transition-all duration-150 ease-out',
            dropzoneDisabled
              ? 'bg-slate-100 border-2 border-slate-300 border-dashed' // Specific disabled state style
              : isDragging
              ? `${dropzoneDragActiveBorder} bg-indigo-100 text-indigo-700` // Dragging state
              : `${dropzoneBaseBorder} bg-white text-slate-700 group-hover:bg-indigo-50 group-hover:border-indigo-500 group-hover:border-solid`, // Default & hover
            // Apply lift effect
            (isDragging || !dropzoneDisabled) && "group-hover:-translate-x-1 group-hover:-translate-y-1",
            isDragging && "-translate-x-1 -translate-y-1" // Lift immediately on drag
          )}
        >
          <UploadCloud className={clsx(
              "mx-auto h-12 w-12 transition-colors",
              dropzoneDisabled ? "text-slate-400" :
              isDragging ? "text-indigo-600" :
              "text-slate-500 group-hover:text-indigo-600"
          )} />
          <div className={clsx(
              "text-sm mt-3 text-center transition-colors",
              dropzoneDisabled ? "text-slate-500" :
              isDragging ? "text-indigo-700" :
              "text-slate-700 group-hover:text-indigo-700"
          )}>
            <span className={clsx(
              "font-semibold",
               !dropzoneDisabled && !isDragging && "group-hover:text-indigo-700 text-indigo-600",
               isDragging && "text-indigo-700"
            )}>
              {t(canAddMoreFiles ? 'fileUpload.uploadAction' : 'fileUpload.maxFilesReached')}
            </span>
            {canAddMoreFiles && <span className={clsx(isDragging ? "text-indigo-600" : "text-slate-600 group-hover:text-indigo-600")}> {t('fileUpload.dragAndDrop')}</span>}
          </div>
          <p className={clsx("text-xs mt-1.5 transition-colors",
              dropzoneDisabled ? "text-slate-400" :
              isDragging ? "text-indigo-500" :
              "text-slate-500 group-hover:text-indigo-500"
          )}>
            {t('fileUpload.fileTypesAndSizeHint', { types: acceptedFileTypes?.split(',').map(t => t.trim().toUpperCase().substring(1)).join(', ') || 'ANY', maxSize: maxFileSizeMB })}
          </p>
          {!canAddMoreFiles && !disabled && (
              <p className="text-xs mt-1.5 text-orange-600 font-semibold">{t('fileUpload.maxFilesLimitWarning', { maxFiles })}</p>
          )}
        </div>
        {!dropzoneDisabled && (
            <input
              ref={fileInputRef}
              id={fieldId} // Use fieldId for the input as well
              name={fieldId}
              type="file"
              className="sr-only"
              multiple
              accept={acceptedFileTypes}
              onChange={onNativeInputChange}
              disabled={dropzoneDisabled}
            />
        )}
      </div>


      {descriptionTextKey && (
        <p className="mt-2 text-xs text-slate-600">{t(descriptionTextKey)}</p>
      )}

      {files.length > 0 && (
        <div className="mt-8 space-y-3">
          <h3 className="text-lg font-semibold text-slate-800">
            {t('fileUpload.uploadedFilesTitle', { count: files.length })}
          </h3>
          {files.map(upFile => (
            <UploadedFileListItem
              key={upFile.id}
              fileData={upFile}
              onRemove={onFileRemoved}
              onRetry={onFileRetry}
            />
          ))}
        </div>
      )}
    </div>
  );
};